import { useEffect, useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { OPS } from "@/constants/testIds";

/**
 * Self-service Notifications dialog.
 * Opens from the Topbar user menu. Currently exposes just the QC-submission
 * email toggle; add rows below as we add more notification types.
 *
 * Persists via `PATCH /api/users/me` — the backend enforces that this route
 * can only flip fields the user is allowed to change (never role/permissions).
 */
export default function NotificationsDialog({ open, onOpenChange }) {
    const { me, refreshMe } = useAuth();
    const { t } = useI18n();
    const currentValue = !!me?.user?.notify_qc_submissions;
    const [value, setValue] = useState(currentValue);
    const [saving, setSaving] = useState(false);

    // Re-sync when the dialog opens so we don't carry stale state across mounts.
    useEffect(() => {
        if (open) setValue(!!me?.user?.notify_qc_submissions);
    }, [open, me]);

    const onSave = async () => {
        setSaving(true);
        try {
            await api.patch("/users/me", { notify_qc_submissions: value });
            if (typeof refreshMe === "function") await refreshMe();
            toast.success(t("notif_saved"));
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={OPS.shell.notifDialog} className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-[color:var(--brand-orange)]" />
                        {t("notif_dialog_title")}
                    </DialogTitle>
                    <DialogDescription>{t("notif_dialog_desc")}</DialogDescription>
                </DialogHeader>

                <div className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <Label className="text-sm font-semibold text-[color:var(--ink)]">
                                {t("notif_qc_submissions_label")}
                            </Label>
                            <p className="text-xs text-[color:var(--ink-muted)] mt-1 leading-relaxed">
                                {t("notif_qc_submissions_desc")}
                            </p>
                        </div>
                        <Switch
                            data-testid={OPS.shell.notifDialogSwitch}
                            checked={value}
                            onCheckedChange={(v) => setValue(!!v)}
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        {t("cancel")}
                    </Button>
                    <Button
                        data-testid={OPS.shell.notifDialogSave}
                        onClick={onSave}
                        disabled={saving}
                        className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                    >
                        {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                        {t("save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
